import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Plus, X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { OrganizationInvitationStatus } from "../enums/OrganizationInvitationStatus";
import {
  selectOrganization,
  selectOrganizationInvitations,
} from "../organization-slice";
import { revokeInvitation } from "../slice-extensions/organization-slice-extension";
import InviteInstructorModal from "./InviteInstructorModal";

/**
 * Pending invitations into the organization, plus the "Invite instructor" action.
 */
const OrganizationInvitationsSection: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const organization = useAppSelector(selectOrganization);
  const invitations = useAppSelector(selectOrganizationInvitations);
  const [inviteOpen, setInviteOpen] = useState(false);

  const pending = invitations.filter(
    (invitation) =>
      invitation.status === OrganizationInvitationStatus.Pending &&
      !invitation.isExpired
  );

  const handleRevoke = (invitationId: string) => {
    if (!organization) return;
    dispatch(revokeInvitation({ organizationId: organization.id, invitationId }));
  };

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-95 text-primary-50 dark:bg-primary-30 dark:text-primary-80">
            <Mail size={20} strokeWidth={2} />
          </span>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold leading-tight">
              {t("organization.invitations.title", "Invitations")}
            </h2>
            <p className="text-sm text-grey-50 dark:text-grey-70 mt-0.5">
              {t("organization.invitations.subtitle", "Pending invitations that haven't been accepted yet.")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setInviteOpen(true)}
          disabled={organization === null}
        >
          <Plus size={18} strokeWidth={2} />
          <span>{t("organization.invitations.invite", "Invite instructor")}</span>
        </button>
      </div>

      {pending.length === 0 ? (
        <p className="mt-4 text-sm text-grey-50 dark:text-grey-70">
          {t("organization.invitations.empty", "No pending invitations.")}
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-grey-90 dark:divide-primary-20">
          {pending.map((invitation) => (
            <li
              key={invitation.id}
              className="flex flex-col gap-2 py-3 lg:flex-row lg:items-center lg:justify-between"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-grey-20 dark:text-white">
                  {invitation.email}
                </p>
                <p className="text-sm text-grey-50 dark:text-grey-70">
                  {t("organization.invitations.row", "{{role}} · expires {{date}}", {
                    role: t(`organization.roles.${invitation.role}`, invitation.role),
                    date: new Date(invitation.expiresAt).toLocaleDateString(),
                  })}
                </p>
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => handleRevoke(invitation.id)}
              >
                <X size={18} strokeWidth={2} />
                <span>{t("organization.invitations.revoke", "Revoke")}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {inviteOpen && organization && (
        <InviteInstructorModal
          organizationId={organization.id}
          onClose={() => setInviteOpen(false)}
        />
      )}
    </div>
  );
};

export default OrganizationInvitationsSection;
